import { useLocation, Link as RouterLink } from 'react-router-dom';
import React from 'react';
import { AppRoutes } from '@consts/routes';
import { Breadcrumbs, Link } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';

/**
 * This is a map of routes to breadcrumb labels.
 * If a route is not in this map, it will not be displayed in the breadcrumb.
 */
const BREADCRUMB_LABELS: Record<string, string> = {
    [AppRoutes.ManageCompany]: 'Manage Company',
    [AppRoutes.ManageReseller]: 'Manage Reseller',
    [AppRoutes.ManageLocation]: 'Manage Location',
    [AppRoutes.Activity]: 'Cut Activity',
    [AppRoutes.Insights]: 'Insights',
    [AppRoutes.Models]: 'Manage Models',
    [AppRoutes.Design]: 'Manage Design',
    [AppRoutes.ManageUser]: 'Manage User',
    [AppRoutes.Users]: 'Manage Users',
    [AppRoutes.CutHistory]: 'Cut History',
    [AppRoutes.Organizations]: 'Manage Organizations',
    [AppRoutes.BatchProcessing]: 'Batch Processing',
    [AppRoutes.Invoices]: 'Invoice Management',
    [AppRoutes.Employees]: 'Employee Ranks',
    [AppRoutes.EmployeeDetails]: 'Employee Details',
    [AppRoutes.BillingChanges]: 'Billing Changes',
    [AppRoutes.StationMoveActivity]: 'Station Move Activity',
    [AppRoutes.AuditTrail]: 'Audit History',
    [AppRoutes.AdminAuditLog]: 'Admin Audit Log',
};

// Map query param values to their corresponding breadcrumb labels.
// Extending static labels allows us to support dynamic manage pages such as
// `/manage/organizations?type=company` → "Manage Company".
const MANAGE_TYPE_LABELS: Record<string, string> = {
    company: 'Manage Company',
    reseller: 'Manage Reseller',
    location: 'Manage Location',
    system: 'Manage System',
};

export const RouteBreadcrumb = () => {
    const location = useLocation();

    // Query params are required for the dynamic "Manage X" breadcrumb.
    const queryParams = new URLSearchParams(location.search);
    const typeParam = queryParams.get('type')?.toLowerCase() ?? '';
    const dynamicLabel = MANAGE_TYPE_LABELS[typeParam];

    const routes = location.pathname.split('/');
    const isUnderOrganizations = location.pathname.startsWith(AppRoutes.Organizations);

    return location.pathname !== AppRoutes.Insights ? (
        <Breadcrumbs sx={{ marginRight: '20px', color: 'white' }}>
            <Link component={RouterLink} to={AppRoutes.Insights} color={'white'}>
                <HomeIcon fontSize={'small'} sx={{ marginTop: '5px' }} />
            </Link>

            {routes.map((route, idx) => {
                const completeRoute = routes.slice(0, idx + 1).join('/');

                const label = BREADCRUMB_LABELS[completeRoute];

                const routeExists = Object.values(AppRoutes).includes(completeRoute as AppRoutes);

                // Determine if this breadcrumb should appear bold. If a dynamic
                // breadcrumb will be appended, that one should appear bold
                // instead of the last static breadcrumb.
                const isLastStatic = idx === routes.length - 1 && !dynamicLabel;

                const nodes: React.ReactNode[] = [];

                if (route && routeExists && label) {
                    nodes.push(
                        <Link
                            component={RouterLink}
                            underline='none'
                            key={completeRoute}
                            color={'white'}
                            to={completeRoute}>
                            {isLastStatic && !dynamicLabel ? <b>{label}</b> : label}
                        </Link>
                    );

                    // Inject dynamic breadcrumb immediately after Manage Organizations
                    if (completeRoute === AppRoutes.Organizations && dynamicLabel && isUnderOrganizations) {
                        nodes.push(
                            <Link
                                component={RouterLink}
                                underline='none'
                                key={dynamicLabel}
                                color={'white'}
                                to={`${AppRoutes.Organizations}${location.search}`}>
                                {location.pathname === AppRoutes.Organizations ? <b>{dynamicLabel}</b> : dynamicLabel}
                            </Link>
                        );
                    }
                }

                return nodes;
            })}

            {/* Dynamic breadcrumb for root manage org page (no nested path) */}
            {!isUnderOrganizations && location.pathname === AppRoutes.Organizations && dynamicLabel && (
                <Link
                    component={RouterLink}
                    underline='none'
                    key={dynamicLabel}
                    color={'white'}
                    to={`${location.pathname}${location.search}`}>
                    <b>{dynamicLabel}</b>
                </Link>
            )}
        </Breadcrumbs>
    ) : null;
};
